import React, { useRef, useEffect, useState, useCallback } from 'react';

const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
         viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const SpinnerIcon = () => (
    <svg width="60" height="60" viewBox="0 0 24 24" fill="white">
      <path d="M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,19a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z" opacity=".25"/>
      <path d="M10.14,1.16a11,11,0,0,0-9,8.92A1.59,1.59,0,0,0,2.46,12,1.52,1.52,0,0,0,4.11,10.7a8,8,0,0,1,6.66-6.61A1.42,1.42,0,0,0,12,2.69h0A1.57,1.57,0,0,0,10.14,1.16Z">
        <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.75s" repeatCount="indefinite"/>
      </path>
    </svg>
);

async function pickDeviceId(preferBack = true) {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter(d => d.kind === 'videoinput');
    if (preferBack) {
      const rearCamera = videoDevices.find(d =>
        /back|rear|environment/i.test(d.label) && !/wide|depth|macro/i.test(d.label)
      );
      if (rearCamera) return rearCamera.deviceId;

      // Exclusion filter was too aggressive — retry without it
      const anyRearCamera = videoDevices.find(d => /back|rear|environment/i.test(d.label));
      if (anyRearCamera) return anyRearCamera.deviceId;
    }
    // Last resort: first available camera
    return videoDevices[0]?.deviceId;
  } catch (err) {
    console.error("Error picking device ID:", err);
    return null;
  }
}


export default function QRScanner({ open, onClose, onDetected }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const playingHandlerRef = useRef(null);
  const frameCheckTimeoutRef = useRef(null);
  const scanPauseTimeoutRef = useRef(null);
  const onDetectedRef = useRef(onDetected);
  onDetectedRef.current = onDetected;

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [stream, setStream] = useState(null);
  const [isJsQRReady, setIsJsQRReady] = useState(false);

  // Load jsQR from CDN once (fallback path)
  useEffect(() => {
    const id = 'jsqr-script';
    if (window.jsQR || document.getElementById(id)) {
      setIsJsQRReady(true);
      return;
    }
    const s = document.createElement('script');
    s.id = id;
    s.src = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js';
    s.async = true;
    s.onload = () => setIsJsQRReady(true);
    s.onerror = () => setError('Failed to load QR library (jsQR). Check connectivity.');
    document.body.appendChild(s);
  }, []);

  const stopCamera = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (scanPauseTimeoutRef.current) {
      clearTimeout(scanPauseTimeoutRef.current);
      scanPauseTimeoutRef.current = null;
    }
    if (frameCheckTimeoutRef.current) {
      clearTimeout(frameCheckTimeoutRef.current);
      frameCheckTimeoutRef.current = null;
    }
    if (playingHandlerRef.current && videoRef.current) {
      videoRef.current.removeEventListener('playing', playingHandlerRef.current);
      playingHandlerRef.current = null;
    }
    // Read tracks directly from the video element to avoid stale closure on `stream` state
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
    }
    setStream(null);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.removeAttribute('src');
      videoRef.current.srcObject = null;
    }
  }, []);

  const handleClose = () => {
    stopCamera();
    setError('');
    onClose?.();
  };

  const startCamera = useCallback(async (deviceId = null) => {
    setIsLoading(true);
    setError('');

    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Camera not supported on this device.');
      setIsLoading(false);
      return;
    }

    try {
      const constraints = {
        video: {
          width: { ideal: 960 },
          height: { ideal: 540 },
        },
        audio: false
      };
      if (deviceId) {
        constraints.video.deviceId = { exact: deviceId };
      } else {
        constraints.video.facingMode = { ideal: 'environment' };
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);

      const video = videoRef.current;
      if (!video) return;

      video.srcObject = mediaStream;
      video.setAttribute('playsinline', 'true');
      video.muted = true;

      const onPlaying = () => {
        if (frameCheckTimeoutRef.current) clearTimeout(frameCheckTimeoutRef.current);
        setIsLoading(false);
        if ('BarcodeDetector' in window) {
          scanWithBarcodeDetector();
        } else {
          waitForJsQRThenScan(4000);
        }
      };

      playingHandlerRef.current = onPlaying;
      video.addEventListener('playing', onPlaying, { once: true });

      try {
        await video.play();
      } catch (e) {
        console.error('Video play failed:', e);
        setError('Failed to start video playback.');
        stopCamera();
        setIsLoading(false);
        return;
      }

      // Sanity-check for black frames and retry if needed
      frameCheckTimeoutRef.current = setTimeout(async () => {
        if (!video.videoWidth) {
          console.warn("Camera started but produced no frames. Attempting retry with specific deviceId.");
          stopCamera();
          const newDeviceId = await pickDeviceId(true);
          if (newDeviceId) {
            startCamera(newDeviceId);
          } else {
            setError('Camera started but produced no frames. Could not find another camera to retry.');
            setIsLoading(false);
          }
        }
      }, 1200);

    } catch (err) {
      console.error('Camera access error:', err);
      let msg = 'Could not access camera.';
      if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') {
        msg = 'Camera permission denied. Please allow camera access.';
      } else if (err?.name === 'NotFoundError' || err?.name === 'OverconstrainedError') {
        msg = 'No suitable camera found on this device.';
      }
      setError(msg);
      setIsLoading(false);
    }
  }, [stopCamera]);

  const finish = useCallback((text) => {
    stopCamera();
    onDetectedRef.current?.(text);
  }, [stopCamera]);

  const waitForJsQRThenScan = (timeoutMs) => {
    const start = performance.now();
    const poll = () => {
      if (window.jsQR) return scanWithJsQR();
      if (performance.now() - start > timeoutMs) {
        setError('No scanner available (BarcodeDetector unsupported & jsQR failed to load).');
        setIsLoading(false);
        return;
      }
      rafRef.current = requestAnimationFrame(poll);
    };
    rafRef.current = requestAnimationFrame(poll);
  };

  const scanWithBarcodeDetector = () => {
    const video = videoRef.current;
    if (!video) return;

    let detector;
    try {
      detector = new window.BarcodeDetector({ formats: ['qr_code'] });
    } catch (err) {
      console.warn('BarcodeDetector instantiation failed, falling back to jsQR');
      waitForJsQRThenScan(0);
      return;
    }

    let consecutiveErrors = 0;

    const loop = async () => {
      try {
        if (!videoRef.current) return;
        const codes = await detector.detect(videoRef.current);
        consecutiveErrors = 0;
        const value = codes?.[0]?.rawValue;

        if (value) {
          finish(value);
          scanPauseTimeoutRef.current = setTimeout(() => {
            scanPauseTimeoutRef.current = null;
            rafRef.current = requestAnimationFrame(loop);
          }, 2000);
          return;
        }
      } catch {
        // Fall back to jsQR after repeated failures (broken BarcodeDetector implementation)
        if (++consecutiveErrors >= 5) {
          waitForJsQRThenScan(4000);
          return;
        }
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
  };

  const scanWithJsQR = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    let frameCount = 0;

    const loop = () => {
      if (!videoRef.current) return;

      // Process 1 out of every 6 frames (~5 scans/sec at 30fps)
      if (frameCount++ % 6 !== 0) {
        rafRef.current = requestAnimationFrame(loop);
        return;
      }

      const vw = video.videoWidth, vh = video.videoHeight;
      if (vw && vh) {
        // Cap scan resolution at 640px wide to reduce CPU load on low-end devices.
        // The video element itself stays full-res for display.
        const sw = Math.min(vw, 640);
        const sh = Math.round(sw * vh / vw);
        canvas.width = sw;
        canvas.height = sh;
        ctx.drawImage(video, 0, 0, sw, sh);
        const img = ctx.getImageData(0, 0, sw, sh);
        const code = window.jsQR?.(img.data, img.width, img.height, {
          inversionAttempts: 'dontInvert',
        });

        if (code?.data) {
          finish(code.data);
          scanPauseTimeoutRef.current = setTimeout(() => {
            scanPauseTimeoutRef.current = null;
            rafRef.current = requestAnimationFrame(loop);
          }, 2000);
          return;
        }
      }

      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  };

  useEffect(() => {
    if (open) {
      startCamera();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  }, [open, startCamera, stopCamera]);

  useEffect(() => {
    const onVis = () => {
      if (document.hidden) {
        stopCamera();
      } else if (open) {
        startCamera();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [open, startCamera, stopCamera]);

  if (!open) return null;

  return (
      <div style={styles.dialogBackdrop}>
        <div style={styles.dialogContent}>
          <button onClick={handleClose} style={styles.closeButton}><CloseIcon /></button>

          {isLoading && (
              <div style={styles.centeredContent}>
                <SpinnerIcon />
                <h3 style={{ ...styles.title, marginTop: 20 }}>Starting camera…</h3>
              </div>
          )}

          {error && (
              <div style={styles.centeredContent}>
                <h3 style={{ ...styles.title, color: '#ff6b6b' }}>Error</h3>
                <p style={styles.bodyText}>{error}</p>
                <div style={{ marginTop: 20 }}>
                  <button onClick={startCamera} style={styles.primaryButton}>Try Again</button>
                </div>
              </div>
          )}

          {/* The video element is always in the DOM when a stream is present,
            to ensure it can start playing in the background. */}
          {!error && stream && (
              <>
                <video
                    ref={videoRef}
                    style={{ ...styles.video, opacity: isLoading ? 0 : 1 }}
                    muted
                    autoPlay
                    playsInline
                />
                {!isLoading && (
                    <>
                      <div style={styles.scanBox} />
                      <p style={styles.scanPrompt}>Point camera at a QR code</p>
                    </>
                )}
              </>
          )}

          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
      </div>
  );
}

const styles = {
  dialogBackdrop: {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)',
    display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 100,
  },
  dialogContent: {
    position: 'relative', backgroundColor: '#1a1a1a', width: '100%', height: '100%',
    display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
    overflow: 'hidden',
  },
  closeButton: {
    position: 'absolute', top: 20, right: 20, background: 'rgba(0,0,0,0.5)', border: 'none',
    borderRadius: '50%', width: 40, height: 40, display: 'flex', justifyContent: 'center',
    alignItems: 'center', color: 'white', cursor: 'pointer', zIndex: 10,
  },
  centeredContent: {
    color: 'white', textAlign: 'center', padding: 20, display: 'flex',
    flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  },
  title: { fontSize: 24, margin: '0 0 15px 0' },
  bodyText: { fontSize: 16, marginBottom: 30, maxWidth: 300, opacity: 0.8 },
  primaryButton: {
    backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: 8,
    padding: '15px 30px', fontSize: 18, cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center', minWidth: 180,
  },
  disabledButton: { backgroundColor: '#555', cursor: 'not-allowed' },
  video: { width: '100%', height: '100%', objectFit: 'cover', transition: 'opacity 0.3s ease-in-out' },
  scanBox: {
    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    width: 'min(70vw, 250px)', height: 'min(70vw, 250px)', border: '3px solid #fff',
    borderRadius: 16, boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)', pointerEvents: 'none',
  },
  scanPrompt: {
    position: 'absolute', top: 'calc(50% + min(35vw, 125px) + 20px)', color: '#fff',
    backgroundColor: 'rgba(0,0,0,0.7)', padding: '10px 20px', borderRadius: 10,
  },
};
